import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardList, FileSearch, Map as MapaIcone, Plus, Search, X } from 'lucide-react';
import { useContexto } from '@/hooks/useContexto';
import { useSessao } from '@/hooks/useSessao';
import {
  useCriarInventario,
  useGerarCiclico,
  useInventarios,
  type CriterioCiclico,
  type InventarioResumo,
  type SelecaoCiclica,
  type TipoInventario,
} from '@/hooks/inventario';
import { GeradorCiclico } from '@/componentes/inventario/GeradorCiclico';
import { SeloTipo } from '@/componentes/inventario/SeloTipo';
import { ErroHttp } from '@/lib/api';
import { cn, dataCurta, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { CabecalhoPagina, Campo, Cartao, Esqueleto, Kpi, Pilula } from '@/componentes/ui/primitivos';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Gaveta } from '@/componentes/ui/gaveta';
import {
  COR_TOM,
  ROTULO_ETAPA_CURTO,
  haQuanto,
  percentualBipado,
  semaforoDoInventario,
  type Tom,
} from '@/componentes/inventario/semaforo';
import type { EtapaEscopo } from '@/hooks/inventario';

/**
 * Inventários do centro.
 *
 * É a porta de entrada do módulo: o menu tem UM item, "Inventário", e tudo
 * o mais (escopos, congelar, contagens, fechamentos, integração, consultas)
 * mora dentro de cada inventário. Uma árvore de três níveis no menu lateral
 * custaria um clique por nível e não caberia no menu recolhido.
 *
 * Criar um inventário é dar o nome e confirmar empresa e centro padrão (vêm
 * do cabeçalho); os escopos nascem lá dentro, a qualquer momento.
 */
export function Inventarios(): JSX.Element {
  const { centro } = useContexto();
  const { temPermissao } = useSessao();
  const lista = useInventarios(centro);
  const [criando, definirCriando] = React.useState(false);
  const [aberto, definirAberto] = React.useState<number | null>(null);
  const podePlanejar = temPermissao('inventario.planejar');

  const [url, definirUrl] = useSearchParams();
  const ver = (url.get('ver') as Filtro | null) ?? 'andamento';
  const [busca, definirBusca] = React.useState('');

  const inventarios = lista.data?.inventarios ?? [];
  const termo = busca.trim().toUpperCase();
  const numeroBuscado = termo.replace('#', '');
  const buscados = termo
    ? inventarios.filter(
        (i) =>
          i.nome.toUpperCase().includes(termo) ||
          String(i.id) === numeroBuscado ||
          i.numerosEscopos.some((e) => String(e) === numeroBuscado),
      )
    : inventarios;
  const emAndamento = buscados.filter((i) => !finalizado(i));
  const finalizados = buscados.filter(finalizado);

  return (
    <div className="space-y-4 p-3 sm:p-5 sm:pl-8">
      <CabecalhoPagina
        sobrelinha="Estoque"
        titulo="Inventário"
        descricao="Cada inventário reúne os escopos do ERP que fazem parte dele. O andamento das contagens aparece somado, sem precisar perguntar quais escopos são de qual inventário."
        icone={<ClipboardList className="h-5 w-5" />}
        acoes={
          podePlanejar && !criando ? (
            <Botao variante="primaria" onClick={() => definirCriando(true)} disabled={centro === null}>
              <Plus className="h-4 w-4" />
              Novo inventário
            </Botao>
          ) : null
        }
      />

      {criando && centro !== null ? (
        <NovoInventario centro={centro} aoCancelar={() => definirCriando(false)} />
      ) : null}

      {lista.isLoading ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Esqueleto key={i} className="h-40" />
          ))}
        </div>
      ) : lista.isError ? (
        <EstadoErro erro={lista.error} aoTentarDeNovo={() => void lista.refetch()} />
      ) : inventarios.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum inventário neste centro"
          detalhe={
            podePlanejar
              ? 'Crie o primeiro dando um nome a ele. Os escopos que já existem no PBS podem ser trazidos para dentro depois.'
              : 'Quem planeja inventários ainda não criou nenhum para este centro.'
          }
          icone={<ClipboardList className="h-7 w-7" />}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex rounded-lg border border-borda bg-superficie2 p-0.5">
              {(
                [
                  ['andamento', 'Em andamento', inventarios.filter((i) => !finalizado(i)).length],
                  ['finalizados', 'Finalizados', inventarios.filter(finalizado).length],
                  ['todos', 'Todos', inventarios.length],
                ] as [Filtro, string, number][]
              ).map(([chave, rotulo, n]) => (
                <button
                  key={chave}
                  type="button"
                  onClick={() => {
                    const proximo = new URLSearchParams(url);
                    proximo.set('ver', chave);
                    definirUrl(proximo, { replace: true });
                  }}
                  className={cn(
                    'inline-flex min-h-[36px] items-center gap-1.5 rounded-md px-3 text-[13px] font-medium',
                    ver === chave ? 'bg-superficie text-texto shadow-xs ring-1 ring-borda' : 'text-textoFraco',
                  )}
                >
                  {rotulo}
                  <span className="tabular rounded-full bg-superficie3 px-1.5 text-2xs font-bold">{n}</span>
                </button>
              ))}
            </div>
            <label className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textoSuave" />
              <input
                value={busca}
                onChange={(e) => definirBusca(e.target.value)}
                placeholder="Buscar por nome, nº ou escopo"
                className="min-h-toque w-full rounded-lg border border-borda bg-superficie pl-9 pr-3 text-sm focus:border-primaria focus:outline-none"
              />
            </label>
          </div>

          {ver !== 'finalizados' ? (
            <Secao titulo="Em andamento" vazio="Nenhum inventário em andamento." itens={emAndamento} aoAbrir={definirAberto} />
          ) : null}
          {ver !== 'andamento' ? (
            <Secao
              titulo="Finalizados"
              vazio="Nenhum inventário finalizado."
              itens={finalizados}
              aoAbrir={definirAberto}
              apagado
            />
          ) : null}
        </>
      )}

      <ResumoInventario
        inventario={inventarios.find((i) => i.id === aberto) ?? null}
        aoFechar={() => definirAberto(null)}
      />
    </div>
  );
}

type Filtro = 'andamento' | 'finalizados' | 'todos';

/** Finalizado = encerrado, ou com todos os escopos integrados ao estoque. */
function finalizado(i: InventarioResumo): boolean {
  return i.situacao === 'ENCERRADO' || (i.escopos > 0 && i.integrados === i.escopos);
}

function Secao({
  titulo,
  vazio,
  itens,
  aoAbrir,
  apagado = false,
}: {
  titulo: string;
  vazio: string;
  itens: InventarioResumo[];
  aoAbrir: (id: number) => void;
  apagado?: boolean;
}): JSX.Element {
  return (
    <section className="space-y-2">
      <h2 className="flex items-center gap-2 text-[13px] font-semibold text-textoFraco">
        {titulo}
        <span className="tabular rounded-full bg-superficie3 px-1.5 text-2xs font-bold">{numero(itens.length)}</span>
      </h2>
      {itens.length === 0 ? (
        <p className="rounded-xl border border-dashed border-borda px-4 py-6 text-center text-sm text-textoSuave">
          {vazio}
        </p>
      ) : (
        // auto-rows-fr: todas as linhas com a altura do cartão mais alto, para
        // inventário com 3 escopos e com 25 terem o mesmo tamanho.
        <div className={cn('grid auto-rows-fr gap-3 md:grid-cols-2 xl:grid-cols-3', apagado && 'opacity-85')}>
          {itens.map((i) => (
            <CartaoInventario key={i.id} inventario={i} aoAbrir={() => aoAbrir(i.id)} />
          ))}
        </div>
      )}
    </section>
  );
}

const ORDEM_ETAPAS: EtapaEscopo[] = ['montagem', 'contagem', 'recontagem', 'final', 'integracao', 'integrado'];

/**
 * Todos os números de escopo no cartão, quebrando linha — o cíclico por rua
 * gera ~25 escopos e a linha única cortava com reticências. Cada número vem na
 * cor do % bipado dele (mesma regra dos tracinhos); o % fica no `title`.
 */
function NumerosEscopos({ inventario: i }: { inventario: InventarioResumo }): JSX.Element | null {
  if (i.numerosEscopos.length === 0) return null;
  const doEscopo = new Map(i.porEscopo.map((e) => [e.escopo, e]));
  return (
    <div className="mt-2">
      <p className="text-2xs text-textoSuave">
        <b className="tabular text-textoFraco">{numero(i.numerosEscopos.length)}</b>{' '}
        {i.numerosEscopos.length === 1 ? 'escopo' : 'escopos'}
      </p>
      <ul className="mt-1 flex flex-wrap gap-1">
        {i.numerosEscopos.map((n) => {
          const e = doEscopo.get(n);
          const p = e ? percentualBipado(e.bipados, e.alvo) : null;
          const tom: Tom = !e || !p ? 'neutro' : e.etapa === 'integrado' ? 'ok' : p.tom;
          return (
            <li
              key={n}
              title={e && p ? `#${n} · ${ROTULO_ETAPA_CURTO[e.etapa]} · ${p.pct}%` : `#${n} · montagem`}
              className={cn('tabular rounded px-1 py-px text-2xs font-semibold', COR_TOM[tom].fundo, COR_TOM[tom].texto)}
            >
              {n}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Todos os escopos do inventário, um ladrilho por escopo com o % e a etapa,
 * na cor do semáforo. Ordem: os que precisam de atenção primeiro (em contagem
 * e vermelho), depois por número. Escopo ainda em montagem não tem foto, então
 * não tem % — aparece cinza.
 */
function GradeEscopos({ inventario: i }: { inventario: InventarioResumo }): JSX.Element | null {
  if (i.numerosEscopos.length === 0) return null;
  const doEscopo = new Map(i.porEscopo.map((e) => [e.escopo, e]));
  const PESO: Record<Tom, number> = { erro: 0, alerta: 1, neutro: 2, ok: 3 };
  const ladrilhos = i.numerosEscopos
    .map((n) => {
      const e = doEscopo.get(n);
      if (!e) return { escopo: n, pct: null as number | null, tom: 'neutro' as Tom, etapa: 'montagem' as EtapaEscopo };
      const p = percentualBipado(e.bipados, e.alvo);
      return { escopo: n, pct: p.pct, tom: e.etapa === 'integrado' ? ('ok' as Tom) : p.tom, etapa: e.etapa };
    })
    .sort((a, b) => PESO[a.tom] - PESO[b.tom] || a.escopo - b.escopo);
  return (
    <div>
      <p className="rotulo mb-1.5">
        Escopos <span className="tabular">({numero(ladrilhos.length)})</span>
      </p>
      <ul className="grid grid-cols-3 gap-1.5 sm:grid-cols-4">
        {ladrilhos.map((l) => (
          <li
            key={l.escopo}
            className={cn('rounded-lg border-l-[3px] border-current px-2 py-1', COR_TOM[l.tom].fundo, COR_TOM[l.tom].texto)}
          >
            <p className="tabular text-[12px] font-bold">#{l.escopo}</p>
            <p className="flex items-baseline justify-between gap-1 text-2xs text-textoFraco">
              <span className="truncate">{ROTULO_ETAPA_CURTO[l.etapa]}</span>
              {l.pct !== null ? <span className="tabular font-semibold text-texto">{l.pct}%</span> : null}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Um tracinho por escopo, na cor do % DELE, logo abaixo da barra geral.
 *
 * O % do cartão é a média, e a média esconde o escopo atrasado: três em 100%
 * e um em 20% dão "80% bipado" em âmbar, com uma rua inteira parada. Os
 * tracinhos mostram isso de relance; o número de cada um fica no `title`.
 *
 * Escopo integrado conta como pronto (verde), como no semáforo. A frase
 * "#2127 atrasado" só aparece quando um escopo AINDA EM CONTAGEM está
 * vermelho e a média não está — é o caso em que o cartão engana.
 *
 * Com um escopo só não aparece: repetiria a barra de cima.
 */
function EscoposNaBarra({
  inventario: i,
  tomGeral,
}: {
  inventario: InventarioResumo;
  tomGeral: Tom;
}): JSX.Element | null {
  if (i.porEscopo.length < 2) return null;
  const escopos = i.porEscopo.map((e) => {
    const p = percentualBipado(e.bipados, e.alvo);
    return { ...e, ...p, tom: e.etapa === 'integrado' ? ('ok' as const) : p.tom };
  });
  const atrasados = tomGeral === 'erro' ? [] : escopos.filter((e) => e.etapa === 'contagem' && e.tom === 'erro');
  // Até 12 por linha; passando disso, quebra em linhas do mesmo tamanho
  // (25 escopos = 3 linhas de 9, não 12 + 12 + 1).
  const linhas = Math.ceil(escopos.length / 12);
  const colunas = Math.ceil(escopos.length / linhas);
  return (
    <>
      <div
        className="mt-1 grid gap-x-0.5 gap-y-1"
        style={{ gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))` }}
        aria-label="Andamento por escopo"
      >
        {escopos.map((e) => (
          <span
            key={e.escopo}
            title={`#${e.escopo} · ${e.pct}% (${numero(e.bipados)}/${numero(e.alvo)})`}
            className={cn('h-1 rounded-full', COR_TOM[e.tom].barra)}
          />
        ))}
      </div>
      {atrasados.length > 0 ? (
        <p className="mt-1 truncate text-2xs text-status-divergencia">
          {atrasados
            .slice(0, 2)
            .map((e) => `#${e.escopo} atrasado (${e.pct}%)`)
            .join(' · ')}
          {atrasados.length > 2 ? ` · +${atrasados.length - 2}` : ''}
        </p>
      ) : null}
    </>
  );
}

/**
 * O cartão responde "como está?" sem abrir nada: o semáforo, quanto foi
 * bipado, quanta divergência já apareceu, onde estão os escopos e se a
 * bipagem está parada. Clicar abre o resumo; dali se vai ao inventário ou
 * ao mapa por rua.
 */
function CartaoInventario({
  inventario: i,
  aoAbrir,
}: {
  inventario: InventarioResumo;
  aoAbrir: () => void;
}): JSX.Element {
  const sem = semaforoDoInventario(i);
  const cor = COR_TOM[sem.tom];
  return (
    <button type="button" onClick={aoAbrir} className="block h-full w-full text-left focus:outline-none">
      {/* Altura mínima cabe ~25 escopos (3 linhas de números): cartões de
          seções diferentes, que não dividem a mesma grade, ficam iguais. */}
      <Cartao interativo className="flex h-full min-h-[22.5rem] flex-col p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-1.5">
              <span className="rotulo">Inventário #{i.id}</span>
              <SeloTipo tipo={i.tipo} criterio={i.criterio} />
            </p>
            <h2 className="mt-0.5 line-clamp-2 text-[15px] font-semibold leading-snug">{i.nome}</h2>
          </div>
          {i.situacao === 'ENCERRADO' ? (
            <Pilula>Encerrado</Pilula>
          ) : (
            <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-2xs font-bold', cor.fundo, cor.texto)}>
              <span className={cn('h-2 w-2 rounded-full', cor.ponto, sem.tom !== 'neutro' && 'animate-pulse')} />
              {sem.rotulo}
            </span>
          )}
        </div>

        <div className="mt-4">
          <div className="flex items-baseline justify-between text-xs text-textoFraco">
            <span title="Endereços com saldo na foto do congelamento. Vazio não precisa de bipe.">
              Bipados · endereços com saldo
            </span>
            <span className="tabular font-semibold text-texto">
              {numero(i.bipadosAlvo)} / {numero(i.alvo)}
            </span>
          </div>
          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-superficie3">
            <span
              className={cn('block h-full rounded-full transition-[width] duration-500', cor.barra)}
              style={{ width: `${Math.max(i.bipadosAlvo > 0 ? 2 : 0, sem.pct)}%` }}
            />
          </div>
          <EscoposNaBarra inventario={i} tomGeral={sem.tom} />
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg bg-superficie2 px-2.5 py-1.5">
            <p className="text-2xs text-textoFraco">Divergências</p>
            <p className={cn('tabular text-sm font-bold', i.divergentes > 0 && 'text-status-atencao')}>
              {numero(i.divergentes)}
            </p>
          </div>
          <div className="rounded-lg bg-superficie2 px-2.5 py-1.5">
            <p className="text-2xs text-textoFraco" title="O coletor envia a contagem quando a pessoa toca em Encerrar.">
              Último envio do coletor
            </p>
            <p className={cn('text-sm font-bold', sem.parado && 'text-status-divergencia')}>
              {haQuanto(i.ultimaColeta)}
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-1">
          {ORDEM_ETAPAS.filter((e) => (i.etapas[e] ?? 0) > 0).map((e) => (
            <span key={e} className="rounded-md border border-borda px-1.5 py-0.5 text-2xs text-textoFraco">
              {ROTULO_ETAPA_CURTO[e]} <b className="tabular text-texto">{i.etapas[e]}</b>
            </span>
          ))}
          {i.escopos === 0 ? <span className="text-2xs text-textoSuave">nenhum escopo ainda</span> : null}
          {i.perdidas > 0 ? (
            <span className="rounded-md bg-status-atencaoFundo px-1.5 py-0.5 text-2xs font-bold text-status-atencao">
              {numero(i.perdidas)} coletas perdidas
            </span>
          ) : null}
        </div>

        <NumerosEscopos inventario={i} />

        <p className="mt-auto pt-3 text-2xs text-textoSuave">
          Criado em {dataCurta(i.criadoEm)}
          {i.criadoPor ? ` · ${i.criadoPor}` : ''}
        </p>
      </Cartao>
    </button>
  );
}

/** O resumo do inventário, sem sair da lista — e os dois caminhos dali. */
function ResumoInventario({
  inventario: i,
  aoFechar,
}: {
  inventario: InventarioResumo | null;
  aoFechar: () => void;
}): JSX.Element {
  const sem = i ? semaforoDoInventario(i) : null;
  return (
    <Gaveta
      aberta={i !== null}
      aoFechar={aoFechar}
      titulo={i?.nome ?? ''}
      subtitulo={i ? `Inventário #${i.id} · centro ${i.centro}` : undefined}
    >
      {i && sem ? (
        <div className="space-y-4 p-5">
          <div className={cn('rounded-xl p-4', COR_TOM[sem.tom].fundo)}>
            <p className={cn('flex items-center gap-2 text-sm font-bold', COR_TOM[sem.tom].texto)}>
              <span className={cn('h-2.5 w-2.5 rounded-full', COR_TOM[sem.tom].ponto)} />
              {sem.rotulo}
            </p>
            {sem.parado ? (
              <p className="mt-1 text-xs text-status-divergencia">
                Escopo em contagem sem envio do coletor {haQuanto(i.ultimaColeta)}. O coletor só envia quando a
                pessoa encerra a contagem — pode haver gente contando agora.
              </p>
            ) : (
              <p className="mt-1 text-xs text-textoFraco">Último envio do coletor {haQuanto(i.ultimaColeta)}.</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Kpi
              rotulo="Bipados (1ª)"
              valor={`${sem.pct}%`}
              nota={`${numero(i.bipadosAlvo)} de ${numero(i.alvo)} endereços com saldo`}
              barra={sem.pct}
              tom={sem.tom === 'neutro' ? 'neutro' : sem.tom}
            />
            <Kpi
              rotulo="Divergências"
              valor={numero(i.divergentes)}
              nota="no último fechamento de cada escopo"
              tom={i.divergentes > 0 ? 'alerta' : 'neutro'}
            />
          </div>

          <p className="text-xs text-textoFraco">
            {numero(i.localizadores)} endereços no inventário; {numero(i.localizadores - i.alvo)} estavam
            vazios na foto e não precisam de bipe.
            {i.sobras > 0 ? (
              <>
                {' '}
                <b className="text-status-atencao">{numero(i.sobras)}</b> vazios tiveram bipe (sobra a
                investigar).
              </>
            ) : null}
          </p>

          <div>
            <p className="rotulo mb-1.5">Escopos por etapa</p>
            <ul className="space-y-1">
              {ORDEM_ETAPAS.map((e) => (
                <li key={e} className="flex items-center justify-between rounded-lg px-2 py-1 text-sm odd:bg-superficie2">
                  <span>{ROTULO_ETAPA_CURTO[e]}</span>
                  <span className="tabular font-semibold">{numero(i.etapas[e] ?? 0)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid gap-2">
            <Botao variante="primaria" comoFilho>
              <Link to={`/inventario/${i.id}`}>
                <ClipboardList className="h-4 w-4" />
                Abrir inventário
              </Link>
            </Botao>
            <Botao variante="secundaria" comoFilho>
              <Link to={`/inventario/${i.id}/mapa`}>
                <MapaIcone className="h-4 w-4" />
                Ver por rua (mapa)
              </Link>
            </Botao>
            <Botao variante="fantasma" comoFilho>
              <Link to={`/inventario/${i.id}/consulta/INVENTARIO_CONTAGEM_DIFERENCAS`}>
                <FileSearch className="h-4 w-4" />
                Diferenças da 1ª contagem
              </Link>
            </Botao>
          </div>

          <GradeEscopos inventario={i} />
        </div>
      ) : null}
    </Gaveta>
  );
}

/**
 * Criar inventário: nome, EMPRESA, centro padrão e TIPO. Empresa e centro
 * vêm preenchidos com os do cabeçalho; o centro padrão é onde o montador abre,
 * e cada escopo pode ir para outro centro da mesma empresa (o inventário 15
 * juntou 2301, 2303, 2305 e 2307).
 *
 *   Geral    como sempre — cria e vai para o inventário montar os escopos.
 *   Cíclico  escolhe por rua ou por SKU, vê a prévia e cria JÁ com os escopos
 *            gerados (um por rua, ou um com as vagas dos SKUs). Congelar
 *            continua sendo o passo seguinte, no inventário.
 */
function NovoInventario({
  centro: centroDoCabecalho,
  aoCancelar,
}: {
  centro: number;
  aoCancelar: () => void;
}): JSX.Element {
  const navegar = useNavigate();
  const criar = useCriarInventario();
  const gerar = useGerarCiclico();
  const { centros, empresas } = useContexto();
  const [empresa, definirEmpresa] = React.useState<number | null>(
    () => centros.find((c) => c.centroEstoque === centroDoCabecalho)?.empresa ?? null,
  );
  const centrosDaEmpresa = centros.filter((c) => c.empresa === empresa);
  const [centro, definirCentro] = React.useState(centroDoCabecalho);
  const escolherEmpresa = (nova: number): void => {
    definirEmpresa(nova);
    // O centro padrão tem de ser da empresa: o do cabeçalho, se for dela; senão o primeiro.
    const daNova = centros.filter((c) => c.empresa === nova);
    definirCentro(daNova.some((c) => c.centroEstoque === centroDoCabecalho) ? centroDoCabecalho : (daNova[0]?.centroEstoque ?? centroDoCabecalho));
  };
  const [nome, definirNome] = React.useState('');
  const [tipo, definirTipo] = React.useState<TipoInventario>('GERAL');
  const [criterio, definirCriterio] = React.useState<CriterioCiclico>('RUA');
  const [selecao, definirSelecao] = React.useState<{ s: SelecaoCiclica | null; grupos: number }>({
    s: null,
    grupos: 0,
  });
  const [problema, definirProblema] = React.useState<string | null>(null);
  const ciclico = tipo === 'CICLICO';
  const valido =
    nome.trim().length >= 3 && empresa !== null && (!ciclico || (selecao.s !== null && selecao.grupos > 0));
  const trabalhando = criar.isPending || gerar.isPending;

  const enviar = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!valido || empresa === null) return;
    definirProblema(null);
    criar.mutate(
      { nome: nome.trim(), empresa, centro, tipo, criterio: ciclico ? criterio : null },
      {
        onSuccess: (r) => {
          if (!ciclico || !selecao.s) {
            // Centro padrão diferente do cabeçalho: o cabeçalho acompanha.
            navegar(`/inventario/${r.id}${centro !== centroDoCabecalho ? `?centro=${centro}` : ''}`);
            return;
          }
          // O inventário já existe: se gerar falhar, ele fica vazio e dá para
          // gerar de novo lá dentro — o erro não se perde.
          gerar.mutate(
            { inventario: r.id, ...selecao.s },
            {
              onSuccess: (g) => navegar(`/inventario/${r.id}${g.pulados.length > 0 ? '?ciclico=pulados' : ''}`),
              onError: () => navegar(`/inventario/${r.id}?ciclico=erro`),
            },
          );
        },
        onError: (err) => definirProblema(err instanceof ErroHttp ? err.message : 'Não foi possível criar o inventário.'),
      },
    );
  };

  const opcao = (ativo: boolean): string =>
    cn(
      'flex-1 rounded-lg border px-3 py-2 text-left transition-colors',
      ativo ? 'border-primaria bg-primaria-suave' : 'border-borda bg-superficie hover:border-bordaForte',
    );

  return (
    <Cartao className="p-4">
      <form onSubmit={enviar} className="space-y-4">
        <label className="block">
          <span className="rotulo mb-1 block">Nome do inventário</span>
          <Campo
            autoFocus
            value={nome}
            maxLength={120}
            onChange={(e) => definirNome(e.target.value)}
            placeholder={ciclico ? 'Ex.: Cíclico semanal — semana 42' : 'Ex.: Inventário Geral Luft Auditoria/2026'}
          />
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="rotulo mb-1 block">Empresa</span>
            <select
              value={empresa ?? ''}
              onChange={(e) => escolherEmpresa(Number(e.target.value))}
              className="min-h-[38px] w-full rounded-lg border border-borda bg-superficie px-2 text-[13px] font-semibold shadow-xs"
            >
              {empresas.map((x) => (
                <option key={x.empresa} value={x.empresa}>
                  {x.empresa}
                  {x.nome ? ` · ${x.nome}` : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="rotulo mb-1 block">Centro padrão dos escopos</span>
            <select
              value={centro}
              onChange={(e) => definirCentro(Number(e.target.value))}
              className="min-h-[38px] w-full rounded-lg border border-borda bg-superficie px-2 text-[13px] font-semibold shadow-xs"
            >
              {centrosDaEmpresa.map((c) => (
                <option key={c.centroEstoque} value={c.centroEstoque}>
                  {c.centroEstoque} · {c.nome}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-[11.5px] text-textoFraco">
              Cada escopo pode ir para outro centro desta empresa, escolhido no montador.
            </span>
          </label>
        </div>

        <fieldset>
          <legend className="rotulo mb-1.5">Tipo</legend>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" className={opcao(!ciclico)} onClick={() => definirTipo('GERAL')} aria-pressed={!ciclico}>
              <span className={cn('block text-[13px] font-bold', !ciclico && 'text-primaria')}>Geral</span>
              <span className="block text-[12px] text-textoFraco">
                Você monta os escopos no montador, endereço a endereço.
              </span>
            </button>
            <button type="button" className={opcao(ciclico)} onClick={() => definirTipo('CICLICO')} aria-pressed={ciclico}>
              <span className={cn('block text-[13px] font-bold', ciclico && 'text-primaria')}>Cíclico</span>
              <span className="block text-[12px] text-textoFraco">
                Escolha ruas ou SKUs e os escopos são gerados automaticamente.
              </span>
            </button>
          </div>
        </fieldset>

        {ciclico ? (
          <fieldset className="space-y-2.5">
            <legend className="rotulo mb-1.5">Contar por</legend>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                className={opcao(criterio === 'RUA')}
                onClick={() => definirCriterio('RUA')}
                aria-pressed={criterio === 'RUA'}
              >
                <span className={cn('block text-[13px] font-bold', criterio === 'RUA' && 'text-primaria')}>Rua</span>
                <span className="block text-[12px] text-textoFraco">
                  Um escopo por rua escolhida, com todos os endereços dela.
                </span>
              </button>
              <button
                type="button"
                className={opcao(criterio === 'SKU')}
                onClick={() => definirCriterio('SKU')}
                aria-pressed={criterio === 'SKU'}
              >
                <span className={cn('block text-[13px] font-bold', criterio === 'SKU' && 'text-primaria')}>SKU</span>
                <span className="block text-[12px] text-textoFraco">
                  Um escopo com as vagas onde os SKUs escolhidos estão.
                </span>
              </button>
            </div>
            <GeradorCiclico
              key={criterio}
              centro={centro}
              criterio={criterio}
              aoMudar={(s, grupos) => definirSelecao({ s, grupos })}
            />
          </fieldset>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 border-t border-borda pt-3">
          {/* Botão desligado precisa dizer por quê — senão parece travado. */}
          <span className={cn('text-[12px]', valido ? 'text-textoFraco' : 'font-semibold text-status-atencao')}>
            {nome.trim().length < 3
              ? 'Dê um nome ao inventário (3 letras ou mais).'
              : ciclico && selecao.s === null
                ? criterio === 'RUA'
                  ? 'Escolha ao menos uma rua.'
                  : 'Escolha ao menos um SKU.'
                : ciclico && selecao.grupos === 0
                  ? 'Nada a gerar com essa escolha — veja o aviso acima.'
                  : ciclico
                    ? 'Cria o inventário com os escopos já gerados. Congelar continua sendo o passo seguinte.'
                    : 'Cria o inventário vazio; os escopos você monta lá dentro.'}
          </span>
          <div className="ml-auto flex gap-2">
            <Botao type="submit" variante="primaria" disabled={!valido} carregando={trabalhando}>
              {ciclico
                ? selecao.grupos > 0
                  ? `Criar e gerar ${numero(selecao.grupos)} ${selecao.grupos === 1 ? 'escopo' : 'escopos'}`
                  : 'Criar e gerar escopos'
                : 'Criar inventário'}
            </Botao>
            <Botao type="button" variante="fantasma" onClick={aoCancelar} aria-label="Cancelar">
              <X className="h-4 w-4" />
            </Botao>
          </div>
        </div>
      </form>
      {problema ? <p className="mt-2 text-sm text-status-divergencia">{problema}</p> : null}
    </Cartao>
  );
}
